import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, ArrowRight, Linkedin, Github, Youtube, Instagram, Facebook, Send as TelegramIcon } from 'lucide-react';
import { personalInfo, statsData } from '../data/mock';

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socialIcons = [
  { icon: Linkedin, href: personalInfo.socialLinks.linkedin, label: 'LinkedIn' },
  { icon: Github, href: personalInfo.socialLinks.github, label: 'GitHub' },
  { icon: XIcon, href: personalInfo.socialLinks.x, label: 'X' },
  { icon: Youtube, href: personalInfo.socialLinks.youtube, label: 'YouTube' },
  { icon: Instagram, href: personalInfo.socialLinks.instagram, label: 'Instagram' },
  { icon: Facebook, href: personalInfo.socialLinks.facebook, label: 'Facebook' },
];

function HexGrid() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;
    let time = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      time += 0.003;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cols = 20;
      const rows = 12;
      const spacing = 50;
      const offsetX = (canvas.width - cols * spacing) / 2;
      const offsetY = (canvas.height - rows * spacing) / 2;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = offsetX + i * spacing + (j % 2) * (spacing / 2);
          const y = offsetY + j * spacing * 0.86;
          const dist = Math.sqrt(Math.pow(x - canvas.width / 2, 2) + Math.pow(y - canvas.height / 2, 2));
          const wave = Math.sin(dist * 0.01 - time * 2) * 0.5 + 0.5;
          const alpha = wave * 0.15 + 0.02;

          ctx.beginPath();
          ctx.arc(x, y, 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0, 229, 255, ${alpha})`;
          ctx.fill();

          if (wave > 0.6) {
            const neighbors = [
              [i + 1, j], [i, j + 1], [i + 1, j + 1]
            ];
            neighbors.forEach(([ni, nj]) => {
              if (ni < cols && nj < rows) {
                const nx = offsetX + ni * spacing + (nj % 2) * (spacing / 2);
                const ny = offsetY + nj * spacing * 0.86;
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(nx, ny);
                ctx.strokeStyle = `rgba(0, 229, 255, ${alpha * 0.3})`;
                ctx.lineWidth = 0.5;
                ctx.stroke();
              }
            });
          }
        }
      }
      animFrame = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.7 }}
    />
  );
}

export default function HeroSection() {
  const [typedText, setTypedText] = useState('');
  const fullText = personalInfo.tagline;

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i <= fullText.length) {
        setTypedText(fullText.slice(0, i));
        i++;
      } else {
        clearInterval(interval);
      }
    }, 55);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Animated background */}
      <HexGrid />

      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#06080d] via-transparent to-[#06080d]" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00e5ff]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-[#00ff88]/3 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 pt-24 pb-16 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left - Content */}
          <div className="flex-1 max-w-2xl">
          {/* Prefix label */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-[1px] bg-[#00e5ff]/50" />
            <span className="font-['JetBrains_Mono'] text-xs tracking-[3px] uppercase text-[#00e5ff]">
              Cybersecurity Leader & AI Strategist
            </span>
          </div>

          {/* Name */}
          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-4 tracking-tight">
            <span className="text-[#f1f5f9]">Vishal</span>
            <br />
            <span className="text-[#00e5ff] glow-cyan">Majithia</span>
          </h1>

          {/* Typed tagline */}
          <div className="min-h-[40px] mb-6">
            <p className="font-['JetBrains_Mono'] text-lg md:text-xl text-[#94a3b8] font-light">
              <span className="text-[#00ff88]">{`> `}</span>
              {typedText}
              <span className="typing-cursor" />
            </p>
          </div>

          {/* Subtitle */}
          <p className="text-base text-[#64748b] max-w-xl mb-10 leading-relaxed">
            {personalInfo.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-4 mb-14">
            <a
              href="#portfolio"
              onClick={(e) => { e.preventDefault(); document.querySelector('#portfolio')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="btn-cyber-filled rounded-md flex items-center gap-2 hover:gap-3 transition-all duration-300"
            >
              View Portfolio <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="btn-cyber rounded-md"
            >
              Get In Touch
            </a>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialIcons.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-10 h-10 rounded-lg border border-[#1e293b] flex items-center justify-center text-[#64748b] hover:text-[#00e5ff] hover:border-[#00e5ff]/40 hover:bg-[#00e5ff]/5 transition-colors duration-300"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
          </div>

          {/* Right - Cinematic Profile Photo */}
          <div className="hidden lg:flex flex-shrink-0 relative">
            {/* Outer glow ring */}
            <div className="absolute -inset-4 bg-gradient-to-br from-[#00e5ff]/20 via-transparent to-[#00ff88]/10 rounded-2xl blur-xl" />
            {/* Main photo container */}
            <div className="relative w-[340px] h-[420px] rounded-2xl overflow-hidden border border-[#00e5ff]/20 shadow-[0_0_60px_rgba(0,229,255,0.12)]">
              {/* Photo */}
              <img
                src={personalInfo.profilePhoto}
                alt="Vishal Majithia"
                className="w-full h-full object-cover object-top"
              />
              {/* Cinematic overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#06080d] via-[#06080d]/20 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#00e5ff]/5 to-transparent" />
              {/* Scanline overlay */}
              <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,229,255,0.03) 2px, rgba(0,229,255,0.03) 4px)'
              }} />
              {/* Corner accents */}
              <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-[#00e5ff]/50" />
              <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-[#00e5ff]/50" />
              <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-[#00e5ff]/50" />
              <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-[#00e5ff]/50" />
              {/* Bottom info bar */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#06080d] to-transparent">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
                  <span className="font-['JetBrains_Mono'] text-[10px] text-[#00ff88] tracking-wider uppercase">Active</span>
                </div>
                <p className="font-['JetBrains_Mono'] text-[10px] text-[#64748b] tracking-wide">
                  SENIOR MANAGER // INTAS GROUP
                </p>
              </div>
              {/* Animated border glow */}
              <div className="absolute inset-0 rounded-2xl border border-[#00e5ff]/10 animate-pulse" style={{ animationDuration: '4s' }} />
            </div>
            {/* Side decoration */}
            <div className="absolute -right-3 top-1/4 w-[2px] h-20 bg-gradient-to-b from-transparent via-[#00e5ff]/40 to-transparent" />
            <div className="absolute -left-3 top-1/2 w-[2px] h-16 bg-gradient-to-b from-transparent via-[#00ff88]/30 to-transparent" />
          </div>
        </div>

        {/* Stats bar */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 border-t border-[#1e293b]/60 pt-10">
          {statsData.map((stat) => (
            <div key={stat.label} className="group">
              <p className="font-['JetBrains_Mono'] text-2xl md:text-3xl font-bold text-[#00e5ff] group-hover:text-[#00ff88] transition-colors duration-300">
                {stat.value}
              </p>
              <p className="text-xs text-[#64748b] mt-1 tracking-wide uppercase font-medium">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-[10px] font-['JetBrains_Mono'] text-[#64748b] tracking-widest uppercase">Scroll</span>
        <ChevronDown className="w-4 h-4 text-[#00e5ff]" />
      </div>
    </section>
  );
}
